import { zodResolver } from "@hookform/resolvers/zod";
import { Checkbox, debounce } from "@mui/material";
import { Box } from "@mui/system";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Link, useLocation, useNavigate } from "react-router-dom";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
	faCheckCircle,
	faChevronDown,
	faChevronRight,
	faChevronUp,
	faExclamationCircle,
	faInfoCircle,
	faPlus,
} from "@fortawesome/pro-regular-svg-icons";

import gridBlocks from "@/assets/images/landing-page/grid blocks.png";

import FormTextField from "@/pages/Private/pages/Account/components/FormTextField";

import { ROUTE_CONFIG } from "@/routes/config";

import HighlightText from "@/components/HighlightText/HighlightText";

import { useGetToDosByFiltersQuery } from "@/pages/Private/redux/to-do/to-do.api";

import { SelfAssessmentStepOption } from "@/types/self-assessment";

import { ToDoPriority } from "@/pages/Private/pages/ToDo/ToDoList/enum/ToDoPriority.enum";

import { Icon } from "@/components";

import { classNames } from "@/utils/classNames";

import { ToDoInfo } from "@/pages/Private/pages/ToDo/ToDoList/schema/to-do";

import ToDoTag from "@/pages/Private/pages/ToDo/ToDoList/components/ToDoTag";

import { Button } from "@/components/Button/Button";

import { Tooltip } from "@/components/Tooltip/Tooltip";

import FormContainer from "../../components/FormContainer";
import { useFinalizeQuestionnaireMutation } from "../../redux/questionnaire/questionnaire.api";
import {
	FinalizeQuestionnaire,
	FinalizeQuestionnaireSchema,
} from "../Questionnaire/schema/questionnaire";

import { QuestionnaireImportance } from "../Questionnaire/enum/QuestionnaireImportance.enum";

const { QUESTIONNAIRE, CHECK_EMAIL, PRIVACY } = ROUTE_CONFIG;

export default function Registration() {
	const { t } = useTranslation();
	const location = useLocation();
	const data = location.state;

	const ts = (key: string, options = {}) => t(`questionnaire.finalize.${key}`, options);
	const [finalize, { error, isLoading, isSuccess }] = useFinalizeQuestionnaireMutation();
	const [errorMessage, setErrorMessage] = useState<string>("");
	const navigate = useNavigate();

	const registrationRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLDivElement>(null);

	const storedAnswers = localStorage.getItem("selfAssessmentAnswers");
	const selfAssessmentAnswers = storedAnswers ? JSON.parse(storedAnswers) : [];

	const [showDescriptionOfTodos, setShowDescriptionOfTodos] = useState<{
		[key: number]: boolean;
	}>({});

	const [showSubTasksOfTodos, setShowSubTasksOfTodos] = useState<{
		[key: number]: boolean;
	}>({});

	const selection = localStorage.getItem("selection")
		? JSON.parse(localStorage.getItem("selection") as string)
		: null;

	const { data: toDos } = useGetToDosByFiltersQuery({
		industryIds: selection?.industryIds ?? [],
		companySizeIds: selection?.companySizeId ? [selection?.companySizeId] : [],
		companyRevenueIds: selection?.companyRevenueId ? [selection?.companyRevenueId] : [],
	});

	const priorityToNumber = (priority: ToDoPriority) => {
		switch (priority) {
			case ToDoPriority.LOW:
				return 1;
			case ToDoPriority.MEDIUM:
				return 2;
		}

		return 3;
	};

	const filteredToDos = useMemo(() => {
		let filteredToDos = [...(toDos || [])];

		filteredToDos = filteredToDos?.filter((toDo) => {
			return !selfAssessmentAnswers.some((answer: SelfAssessmentStepOption) => {
				return answer.questionId === toDo.id && answer.answer === "yes";
			});
		});

		if (filteredToDos && filteredToDos.length < 4) {
			filteredToDos = [...(toDos || [])];
		}

		filteredToDos = filteredToDos?.sort((a, b) => {
			return (
				priorityToNumber(b.priority ?? ToDoPriority.LOW) -
				priorityToNumber(a.priority ?? ToDoPriority.LOW)
			);
		});

		return filteredToDos;
	}, [toDos]);

	const initialValues: FinalizeQuestionnaire = useMemo(() => {
		const storedImportance = localStorage.getItem("importance");

		const validImportanceValues = Object.values(QuestionnaireImportance);

		const importance = validImportanceValues.includes(storedImportance as QuestionnaireImportance)
			? (storedImportance as QuestionnaireImportance)
			: QuestionnaireImportance.IMPORTANT;

		return {
			email: "",
			firstName: "",
			lastName: "",
			companyName: "",
			importance: importance,
			privacyStatement: false,
			selfAssessmentAnswers,
		};
	}, []);

	useEffect(() => {
		if (!data?.selfAssessmentCompleted) {
			setErrorMessage(ts("first-pass-questionnaire"));
		} else {
			setErrorMessage("");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	const {
		register,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm<FinalizeQuestionnaire>({
		defaultValues: initialValues,
		resolver: zodResolver(FinalizeQuestionnaireSchema),
	});

	if (Object.keys(errors).length > 0) {
		console.error("errors", errors);
	}

	const onSubmit = async (values: FinalizeQuestionnaire) => {
		try {
			if (errorMessage) {
				navigate(QUESTIONNAIRE);
			} else {
				await finalize({
					firstName: values.firstName,
					lastName: values.lastName,
					email: values.email,
					companyName: values.companyName,
					importance: values.importance,
					privacyStatement: values.privacyStatement,
					selfAssessmentAnswers: values.selfAssessmentAnswers,
				}).unwrap();
			}
		} catch (err) {
			console.error(err);
		}
	};

	useEffect(() => {
		if (isSuccess) {
			navigate(CHECK_EMAIL, { state: { email: watch("email") } });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccess, navigate]);

	function renderPrioForTodo(toDo: ToDoInfo) {
		let prioClass = "bg-status-error-light";
		let prioTextClass = "text-status-error";
		let prioIcon = faExclamationCircle;

		switch (toDo.priority) {
			case ToDoPriority.LOW:
				prioClass = "bg-status-success";
				prioTextClass = "text-status-success-light";
				prioIcon = faInfoCircle;
				break;
			case ToDoPriority.MEDIUM:
				prioClass = "bg-status-warning";
				prioTextClass = "text-status-warning-light";
				prioIcon = faInfoCircle;
				break;
			case ToDoPriority.HIGH:
				prioClass = "bg-status-error";
				prioTextClass = "text-status-error-light";
				prioIcon = faExclamationCircle;
				break;
		}

		return (
			<div
				className={classNames(
					"absolute top-4 right-6 rounded-md px-2 py-0.5 flex items-center gap-1",
					prioClass
				)}
			>
				<Tooltip content={t(`manage-to-dos.${toDo.priority}-explanation`)}>
					<div className="flex items-center gap-1">
						<Icon
							className={classNames("text-white w-3 h-3 font-semibold", prioTextClass)}
							icon={prioIcon}
						/>
						<span className={classNames("text-white text-xs font-semibold", prioTextClass)}>
							{t(`manage-to-dos.${toDo.priority}`)}
						</span>
					</div>
				</Tooltip>
			</div>
		);
	}

	useEffect(() => {
		if (registrationRef.current) {
			const isElementVisible = () => {
				const rect = registrationRef.current?.getBoundingClientRect();

				if (rect) {
					const isVisible =
						rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);

					if (isVisible && buttonRef.current) {
						buttonRef.current.style.opacity = "0";
					} else if (buttonRef.current) {
						buttonRef.current.style.opacity = "1";
					}
				}
			};

			const handleScroll = debounce(isElementVisible, 300);

			document.addEventListener("scroll", handleScroll);

			return () => {
				document.removeEventListener("scroll", handleScroll);
			};
		}
	}, [registrationRef, buttonRef]);

	const handleScrollToRegistration = useCallback(() => {
		registrationRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [registrationRef]);

	return (
		<>
			<section className="relative w-full bg-surface py-6 sm:py-12 px-4 flex justify-center overflow-hidden">
				<div
					className="absolute inset-0 bg-no-repeat bg-cover bg-center z-0 opacity-[0.04]"
					style={{ backgroundImage: `url(${gridBlocks})` }}
				/>
				<div className="relative z-10 flex flex-col md:flex-row justify-center items-center max-w-[1500px] w-full">
					<div className="flex flex-col max-w-[694px] w-full">
						<div className="flex flex-col gap-6 items-center">
							<h1 className="font-urbanist font-bold text-ink text-xl text-center md:text-lg leading-[120%]">
								{ts("title")}
							</h1>
							<p className="font-urbanist font-medium text-subtle text-center text-md md:text-base leading-[140%]">
								{ts("subtitle")}
							</p>
						</div>
					</div>
				</div>
			</section>
			<div
				ref={buttonRef}
				className="fixed bottom-4 right-4 md:hidden z-50 transition-opacity duration-300"
			>
				<Button title={t("basics.to-registration")} onClick={() => handleScrollToRegistration()} />
			</div>
			<div className="container mx-auto px-4 py-12">
				<div className="max-w-6xl mx-auto  w-full h-full flex flex-col md:flex-row items-start justify-center gap-4 p-0 md:p-4">
					<div className="w-full md:w-1/2 h-full gap-6 flex flex-col">
						<div className="flex items-center gap-2">
							<Icon className="text-primary-blue-dark" icon={faCheckCircle} />
							<h3 className="text-md font-bold text-primary-blue-dark">Ihre ersten 3 Aufgaben</h3>
						</div>
						{filteredToDos?.slice(0, 3)?.length && filteredToDos.slice(0, 3)?.length > 0 ? (
							filteredToDos.slice(0, 3)?.map((toDo) => <div key={toDo.id}>{renderToDo(toDo)}</div>)
						) : (
							<></>
						)}
						{filteredToDos?.length && filteredToDos?.length - 3 > 0 ? (
							<div className="flex items-center gap-4">
								<Icon
									className="text-primary-blue-dark w-3 h-3 bg-gray-200 rounded-full p-1"
									icon={faPlus}
								/>
								<h3 className="text-smd font-semibold text-primary-blue-dark ">
									Weitere {filteredToDos?.length ? filteredToDos?.length - 3 : 0} Aufgaben verfügbar
								</h3>
							</div>
						) : (
							<></>
						)}

						{filteredToDos?.length && filteredToDos?.length > 3 && filteredToDos?.[4] ? (
							<div className="relative [&_.line-clamp-none]:!line-clamp-4 select-none  pointer-events-none">
								<div className="select-none blur-lg pointer-events-none [&_.line-clamp-none]:!line-clamp-4">
									{renderToDo(filteredToDos?.[4])}
								</div>
								<div className="absolute inset-0" />
							</div>
						) : (
							<></>
						)}
					</div>
					<div ref={registrationRef}></div>
					<FormContainer
						changeActionsDirection
						actionContainerClassName="flex gap-2 !space-y-0"
						formContainerClassName="h-fit w-1/2 mt-0 md:mt-0"
						formError={error}
						formFooter={
							errorMessage ? (
								<></>
							) : (
								<>
									<Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
										<Checkbox
											sx={
												errors?.privacyStatement?.message
													? { color: "red", m: 0, p: 0 }
													: { color: "#DCDAD6", m: 0, p: 0 }
											}
											{...register("privacyStatement")}
										/>

										<span className="text-xs sm:text-sm text-primary-gray-lighter inline float-left">
											{t("basics.privacyStatementObject.noLink")}
											<Link
												className="text-progress-bar mr-1 font-normal inline"
												target="_blank"
												to={PRIVACY}
											>
												{t("basics.privacyStatementObject.link1")}
											</Link>
											{t("basics.privacyStatementObject.noLink2")}
										</span>
									</Box>
								</>
							)
						}
						handleSubmit={errorMessage ? () => navigate(QUESTIONNAIRE) : handleSubmit(onSubmit)}
						isLoading={isLoading}
						secondaryButtonType="button"
						showLogo={false}
						submitButtonText={errorMessage ? ts("to-questionnaire") : ts("continue")}
						submitButtonType="button"
						title={errorMessage ? ts("questionnaire-required") : ts("company-registration")}
					>
						{errorMessage ? (
							<HighlightText
								className="text-[14px] mt-6 text-primary-gray-lighter"
								highlights={[]}
								text={errorMessage}
							/>
						) : (
							<>
								<FormTextField
									autoComplete="firstName"
									errorMessage={errors?.firstName?.message}
									formRegister={register("firstName")}
									label={t("basics.firstName")}
									placeHolder={t("basics.enterFirstName")}
								/>
								<FormTextField
									autoComplete="lastName"
									errorMessage={errors?.lastName?.message}
									formRegister={register("lastName")}
									label={t("basics.lastName")}
									placeHolder={t("basics.enterLastName")}
								/>
								<FormTextField
									autoComplete="email"
									errorMessage={errors?.email?.message}
									formRegister={register("email")}
									label={t("basics.email")}
									type="email"
								/>
								<FormTextField
									autoComplete="companyName"
									errorMessage={errors?.companyName?.message}
									formRegister={register("companyName")}
									label={t("basics.companyName")}
									placeHolder={t("basics.enterCompanyName")}
								/>
							</>
						)}
					</FormContainer>
				</div>
			</div>
		</>
	);

	function renderToDo(toDo: ToDoInfo) {
		return (
			<div
				key={toDo.id}
				className={classNames(
					"shadow-lg hover:shadow-xl transition-shadow duration-300 w-full h-full bg-surface rounded-xl p-1 border border-border"
				)}
			>
				<div className="w-full h-full bg-white rounded-lg py-4 px-4 md:px-6 relative">
					{renderPrioForTodo(toDo)}
					<div className="max-w-[calc(100%-74px)] flex items-center gap-2 text-primary-blue-dark font-semibold text-smd leading-7">
						{toDo.title}
					</div>
					<div className="flex items-center gap-2 text-primary-blue font-semibold text-ssm mt-2 mb-4">
						<ToDoTag title={toDo.tag?.name.split("/")?.[0] ?? ""} />
					</div>
					<div
						dangerouslySetInnerHTML={{ __html: toDo.description ?? "" }}
						className={classNames(
							"text-primary-gray cursor-pointer line-clamp-4 !font-normal !text-sm mt-1 max-w-full max-w-full p:!mb-0 p:!mt-0 p:!text-xs/4 p:!text-primary-gray-lighter ul:pl-5 ol:pl-5",
							toDo.id && showDescriptionOfTodos?.[toDo.id] ? "line-clamp-none" : ""
						)}
						onClick={() =>
							toDo.id &&
							setShowDescriptionOfTodos({
								...showDescriptionOfTodos,
								[toDo.id]: !showDescriptionOfTodos?.[toDo.id],
							})
						}
					/>
					<div
						className="flex items-center gap-1 text-primary-gray font-normal !text-xs mt-1 mb-4 cursor-pointer"
						onClick={() =>
							toDo.id &&
							setShowDescriptionOfTodos({
								...showDescriptionOfTodos,
								[toDo.id]: !showDescriptionOfTodos?.[toDo.id],
							})
						}
					>
						{toDo.id && showDescriptionOfTodos?.[toDo.id] ? "Weniger anzeigen" : "Mehr anzeigen"}
					</div>
					<div
						className="flex flex-row items-center justify-between gap-2 cursor-pointer"
						onClick={() =>
							toDo.subTasks?.length &&
							toDo.subTasks?.length > 0 &&
							toDo.id &&
							setShowSubTasksOfTodos({
								...showSubTasksOfTodos,
								[toDo.id]: !showSubTasksOfTodos?.[toDo.id],
							})
						}
					>
						{toDo.subTasks?.length && toDo.subTasks?.length > 0 ? (
							<div className="flex flex-row items-start gap-2 text-primary-gray-lighter font-normal text-ssm items-center  mb-4 cursor-pointer">
								<Icon className="text-primary-gray-lighter w-4 h-4" icon={faCheckCircle} />
								<div>{toDo.subTasks?.length} Umsetzungsschritte</div>
							</div>
						) : (
							<></>
						)}
						{toDo.subTasks?.length && toDo.subTasks?.length > 0 ? (
							<div className="flex items-center gap-1 text-primary-gray-lighter font-normal text-ssm mt-1 mb-4 cursor-pointer">
								<Icon
									className="text-primary-gray-lighter w-4 h-4"
									icon={toDo.id && showSubTasksOfTodos?.[toDo.id] ? faChevronUp : faChevronDown}
								/>
								{toDo.id && showSubTasksOfTodos?.[toDo.id] ? "Weniger anzeigen" : "Mehr anzeigen"}
							</div>
						) : (
							<></>
						)}
					</div>
					{toDo.id && showSubTasksOfTodos?.[toDo.id] ? (
						<div className="flex flex-col gap-2 border-t border-border pt-4">
							<div className="text-primary-gray-lighter gap-2 font-semibold text-ssm mt-1 mb-4 cursor-pointer flex items-center gap-21">
								<Icon className="text-primary-gray-lighter w-4 h-4" icon={faCheckCircle} />
								Umsetzungsschritte
							</div>
							{toDo.subTasks?.map((subTask) => (
								<div
									key={subTask.id}
									className="flex items-start justify-start gap-2 flex-row border border-border pb-2 rounded-lg px-2 py-1 bg-brand-white"
								>
									<div>
										<Icon className="text-primary-blue-dark w-4 h-4" icon={faChevronRight} />
									</div>
									<div className="flex items-start justify-start text-left gap-0 flex-col">
										<div className="text-primary-blue-dark text-left font-semibold text-xs mb-1">
											{subTask.title}
										</div>
										<div
											dangerouslySetInnerHTML={{ __html: subTask.description ?? "" }}
											className="text-primary-blue-dark font-normal text-ssm flex-col flex items-start gap-1 prose prose:max-w-full prose-p:text-xs/4 prose-p:max-w-full prose-p:!mb-0 prose-p:!mt-0 prose-p:!text-primary-gray-lighter prose-ul:pl-5 prose-ol:pl-5"
										/>
									</div>
								</div>
							))}
						</div>
					) : (
						<></>
					)}
				</div>
			</div>
		);
	}
}
